import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Clock, Heart, MessageSquareOff } from 'lucide-react';

const copy = {
  en: {
    heading: 'Most people wait too long.',
    points: [
      { icon: 'clock', text: 'We assume there will always be more time.' },
      { icon: 'heart', text: 'We save our deepest words for funerals.' },
      { icon: 'message', text: 'The people we love often never hear what mattered most.' },
    ],
  },
  es: {
    heading: 'La mayoría espera demasiado.',
    points: [
      { icon: 'clock', text: 'Asumimos que siempre habrá más tiempo.' },
      { icon: 'heart', text: 'Guardamos nuestras palabras más profundas para los funerales.' },
      { icon: 'message', text: 'Las personas que amamos a menudo nunca escuchan lo que más importaba.' },
    ],
  },
  pt: {
    heading: 'A maioria das pessoas espera tempo demais.',
    points: [
      { icon: 'clock', text: 'Assumimos que sempre haverá mais tempo.' },
      { icon: 'heart', text: 'Guardamos nossas palavras mais profundas para funerais.' },
      { icon: 'message', text: 'As pessoas que amamos muitas vezes nunca ouvem o que mais importava.' },
    ],
  },
  fr: {
    heading: 'La plupart des gens attendent trop longtemps.',
    points: [
      { icon: 'clock', text: 'Nous supposons qu\'il y aura toujours plus de temps.' },
      { icon: 'heart', text: 'Nous réservons nos mots les plus profonds pour les funérailles.' },
      { icon: 'message', text: 'Les personnes que nous aimons n\'entendent souvent jamais ce qui comptait le plus.' },
    ],
  },
  ar: { heading: 'معظم الناس ينتظرون طويلاً.', points: [{ icon: 'clock', text: 'نفترض أنه سيكون هناك دائمًا المزيد من الوقت.' }, { icon: 'heart', text: 'نحتفظ بأعمق كلماتنا للجنازات.' }, { icon: 'message', text: 'الأشخاص الذين نحبهم غالبًا لا يسمعون أبدًا ما كان الأهم.' }] },
  ja: { heading: 'ほとんどの人は待ちすぎます。', points: [{ icon: 'clock', text: 'いつでも時間があると思い込んでいます。' }, { icon: 'heart', text: '最も深い言葉をお葬式のために取っておきます。' }, { icon: 'message', text: '愛する人は、最も大切なことを聞かないままです。' }] },
  ko: { heading: '대부분의 사람들은 너무 오래 기다립니다.', points: [{ icon: 'clock', text: '항상 더 많은 시간이 있을 거라고 생각합니다.' }, { icon: 'heart', text: '가장 깊은 말을 장례식을 위해 아껴둡니다.' }, { icon: 'message', text: '우리가 사랑하는 사람들은 가장 중요한 것을 듣지 못하는 경우가 많습니다.' }] },
  id: { heading: 'Kebanyakan orang menunggu terlalu lama.', points: [{ icon: 'clock', text: 'Kita berasumsi akan selalu ada lebih banyak waktu.' }, { icon: 'heart', text: 'Kita menyimpan kata-kata terdalam untuk pemakaman.' }, { icon: 'message', text: 'Orang yang kita cintai sering tidak pernah mendengar apa yang paling penting.' }] },

  hi: {
    heading: 'अधिकांश लोग बहुत लंबा इंतजार करते हैं।',
    points: [
      { icon: 'clock', text: 'We assume there will always be more time.' },
      { icon: 'heart', text: 'We save our deepest words for funerals.' },
      { icon: 'संदेश', text: 'जिन लोगों से हम प्यार करते हैं, वे अक्सर कभी नहीं सुनते कि सबसे महत्वपूर्ण क्या था।' },
    ],
  },
};

const iconMap = {
  clock: Clock,
  heart: Heart,
  message: MessageSquareOff,
};

export function ProblemSection() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="py-20 md:py-28 bg-muted/30">
      <div className="container max-w-4xl mx-auto text-center">
        <motion.h2
          className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-14 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-8">
          {t.points.map((point, i) => {
            const Icon = iconMap[point.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={i}
                className="flex flex-col items-center gap-4 p-6 rounded-2xl bg-card border border-border/30"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
              >
                <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
                  <Icon className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-base md:text-lg text-foreground font-medium leading-relaxed">
                  {point.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
